/**
 * IN SHORT: This file is the 'Factory' that creates your secure identity (Public/Private Keys) in the browser.
 * 
 * RSA key pair generation.
 *
 * ARCHITECTURE DECISIONS:
 *
 * 1. Non-extractable private keys.
 *    We generate two CryptoKey handles from the same logical key pair — one
 *    for signing (RSASSA-PKCS1-v1_5 / RS256) and one for decryption
 *    (RSA-OAEP / RSA-OAEP-256). The Web Crypto API binds each key to a
 *    single algorithm at generation time. We work around this by generating
 *    a key pair, exporting the private JWK (once, in memory, never stored),
 *    and immediately re-importing with `extractable: false` for both
 *    algorithm roles.
 *
 *    After this function returns, the raw private key bytes are GC-eligible.
 *    They cannot be accessed from JavaScript again.
 *
 * 2. Key size: RSA-2048.
 *    RSA-3072 or RSA-4096 would be stronger but are 4–16× slower for
 *    sign/decrypt. For session keys rotated daily, RSA-2048 is the right
 *    tradeoff.  NIST SP 800-57 considers RSA-2048 secure until 2030.
 *
 * 3. Public exponent: 65537 (0x010001).
 *    The only safe choice — never use e=3.
 */

import { generateKeyPair, exportJWK, importJWK, exportSPKI, importSPKI } from 'jose';
import type { JWK } from 'jose';
import { JwtSdkError, JwtSdkErrorCode } from '../errors/index.js';
import type { ClientKeyPair, ServerPublicKeys } from '../types/index.js';
import { RSA_KEY_SIZE, SIGNING_ALG, KEY_WRAP_ALG } from '../types/index.js';
import { nowSeconds } from '../utils/time.js';

function generateKeyId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  // Fallback for environments without randomUUID (rare, e.g. old Node.js).
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export async function generateClientKeyPair(
  ttlSeconds?: number,
): Promise<ClientKeyPair> {
  // Step 1 — Generate extractable key pair for RS256 so we can export the JWK.
  let extractableKeyPair: { publicKey: CryptoKey; privateKey: CryptoKey };
  try {
    extractableKeyPair = await generateKeyPair(SIGNING_ALG, {
      modulusLength: RSA_KEY_SIZE,
      extractable: true, // temporary — we re-import as non-extractable below
    });
  } catch (err) {
    throw JwtSdkError.wrap(JwtSdkErrorCode.KEY_GENERATION_FAILED, 'RSA key pair generation failed', err);
  }

  // Step 2 — Export both keys as JWK while they are still extractable.
  let privateJwk: JWK;
  let publicJwk: JWK;
  try {
    [privateJwk, publicJwk] = await Promise.all([
      exportJWK(extractableKeyPair.privateKey),
      exportJWK(extractableKeyPair.publicKey),
    ]);
  } catch (err) {
    throw JwtSdkError.wrap(JwtSdkErrorCode.KEY_EXPORT_FAILED, 'Key export failed during generation', err);
  }

  // Step 3 — Re-import private key as NON-EXTRACTABLE for each algorithm role.
  // After these imports the raw key material is no longer reachable from JS.
  let signingKey: CryptoKey;
  let decryptionKey: CryptoKey;
  try {
    [signingKey, decryptionKey] = await Promise.all([
      importJWK({ ...privateJwk, alg: SIGNING_ALG }, SIGNING_ALG, { extractable: false }) as Promise<CryptoKey>,
      importJWK({ ...privateJwk, alg: KEY_WRAP_ALG }, KEY_WRAP_ALG, { extractable: false }) as Promise<CryptoKey>,
    ]);
  } catch (err) {
    throw JwtSdkError.wrap(JwtSdkErrorCode.KEY_IMPORT_FAILED, 'Failed to re-import as non-extractable', err);
  }

  // Step 4 — Annotate the public JWK with metadata.
  const keyId = generateKeyId();
  const createdAt = nowSeconds();

  // Omit BOTH `alg` and `key_ops` intentionally — the same RSA public key is
  // imported under two algorithm roles (RS256 for sig verification,
  // RSA-OAEP-256 for encryption). Pinning either field would prevent the Web
  // Crypto API from accepting this JWK for the other algorithm role. Callers
  // pass `alg` explicitly to importJWK at the import site instead.
  const annotatedPublicJwk: JWK = {
    ...publicJwk,
    kid: keyId,
  };

  // Best-effort: null the private exponent reference before GC.
  (privateJwk as Record<string, unknown>)['d'] = null;

  const pair: ClientKeyPair = {
    signingKey,
    decryptionKey,
    publicKeyJwk: annotatedPublicJwk,
    keyId,
    createdAt,
  };
  if (ttlSeconds !== undefined) {
    pair.expiresAt = createdAt + ttlSeconds;
  }
  return pair;
}

/**
 * Re-hydrate a ClientKeyPair from a stored private JWK.
 * SECURITY WARNING: Only call this with JWK material from a trusted local
 * source. Never pass user-controlled input here.
 */
export async function rehydrateClientKeyPairFromJwk(
  privateJwk: JWK,
  publicJwk: JWK,
  keyId: string,
  createdAt: number,
  expiresAt?: number,
): Promise<ClientKeyPair> {
  try {
    const [signingKey, decryptionKey] = await Promise.all([
      importJWK({ ...privateJwk, alg: SIGNING_ALG }, SIGNING_ALG, { extractable: false }) as Promise<CryptoKey>,
      importJWK({ ...privateJwk, alg: KEY_WRAP_ALG }, KEY_WRAP_ALG, { extractable: false }) as Promise<CryptoKey>,
    ]);

    const pair: ClientKeyPair = { signingKey, decryptionKey, publicKeyJwk: publicJwk, keyId, createdAt };
    if (expiresAt !== undefined) {
      pair.expiresAt = expiresAt;
    }
    return pair;
  } catch (err) {
    throw JwtSdkError.wrap(JwtSdkErrorCode.KEY_IMPORT_FAILED, 'Failed to rehydrate key pair from JWK', err);
  }
}

/**
 * Export a public JWK to SPKI PEM format.
 */
export async function exportPublicKeyAsPem(jwk: JWK): Promise<string> {
  try {
    const key = await importJWK(jwk, SIGNING_ALG) as CryptoKey;
    return await exportSPKI(key);
  } catch (err) {
    throw JwtSdkError.wrap(JwtSdkErrorCode.KEY_EXPORT_FAILED, 'Failed to export public key as PEM', err);
  }
}

/**
 * Import a server SPKI PEM public key for verification and encryption.
 */
export async function importServerPublicKeyFromPem(
  pem: string,
  ttlSeconds = 3600,
): Promise<ServerPublicKeys> {
  try {
    const [verificationKey, encryptionKey] = await Promise.all([
      importSPKI(pem, SIGNING_ALG),// same PEM → imported for RS256 (verify)
      importSPKI(pem, KEY_WRAP_ALG),// same PEM → imported for RSA-OAEP (encrypt)
    ]);
    const now = nowSeconds();
    return {
      verificationKey,
      encryptionKey,
      fetchedAt: now,
      expiresAt: now + ttlSeconds,
    };
  } catch (err) {
    throw JwtSdkError.wrap(JwtSdkErrorCode.KEY_IMPORT_FAILED, 'Failed to import server public key from PEM', err);
  }
}
